// Pressing a key on an IR remote — the whole of the behaviour, so both shells
// draw the same pads over the same rules.
//
// ⚠️ WHY A SEND LOCK, AND WHY IT MUST NOT BE DELETED AS AN OVER-CAUTIOUS DEBOUNCE.
//
// The power keys are TOGGLES. `TVON` is labelled "ON/OFF" in the Flutter source
// and there is no TVOFF or DNOFF anywhere in it — one token, and the firmware
// flips. So a key that arrives TWICE undoes itself: the television turns on and
// straight back off, which reads as "the remote does not work".
//
// Two ways it arrives twice:
//   • a human double-tap, which on a remote is a completely normal thing to do
//   • QoS 1. This app publishes at QoS 1 (core/mqtt/mqttClient.js) where Flutter
//     used QoS 0, and at-least-once means a redelivery is not a bug — it is the
//     contract. Nothing downstream de-duplicates, because a relay command is
//     idempotent and this one is not.
//
// The lock is per DEVICE, not per key: two different keys inside the window are
// also suppressed, because the blaster transmits serially and a second flash on
// top of the first is what confuses the appliance in the first place.
//
// ⚠️ AND NOTHING IS OPTIMISTIC. A blaster's output is open-loop — it cannot know
// whether the television saw the flash, and it does not report state. Any tick,
// toast or highlight the UI shows would be believed, and would be a guess. All
// that is offered is the device's own ACK sentence, verbatim, plus the honest
// "have we heard from it" timestamp.

import { useCallback, useRef, useState } from 'react';
import { useApp } from '../state/AppContext';
import { publishCommand } from '../utils/deviceController';
import { useDeviceStatus } from '../mqtt/useDeviceStatus';

// Long enough to swallow a fumbled double-tap and a QoS-1 redelivery; short
// enough that deliberate repeats (volume, channel) still feel like a remote.
export const IR_SEND_LOCK_MS = 400;

export function useIrRemote(uniqueNumber, initialStatus = '') {
  const { userId } = useApp();
  // Seeded from the row and then live. For a blaster this is an ACK SENTENCE
  // ("TV ON / OFF Done"), never a state — see the ⚠️ in core/devices/irRemote.
  const [status, , liveAt] = useDeviceStatus(uniqueNumber, initialStatus);
  const [lastSent, setLastSent] = useState(null);
  const lockedUntil = useRef(0);

  const send = useCallback((token) => {
    if (!token || !uniqueNumber) return false;
    const now = Date.now();
    if (now < lockedUntil.current) return false;
    lockedUntil.current = now + IR_SEND_LOCK_MS;

    publishCommand(uniqueNumber, token, userId);
    // What was SENT, not what happened — the wording matters, because the two
    // are genuinely different things here and only one of them is knowable.
    setLastSent({ token, at: now });
    return true;
  }, [uniqueNumber, userId]);

  return {
    send,
    lastSent,
    /** The device's own words, for display only. Never parsed. */
    lastAck: String(status || '').trim(),
    /** null until a frame has actually arrived on the wire this session. */
    liveAt,
  };
}

export default useIrRemote;
